import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
// Plugins
import { NzSelectModeType } from 'ng-zorro-antd/select';
import { NzTableQueryParams } from 'ng-zorro-antd/table';
import { Subscription, timer } from 'rxjs';
// Models
import { Account } from '@models/account/account.model';
import { AccountsRO } from '@models/account/account.model.ro';
// Services
import { AccountService } from '@services/account/account.service';

@Component({
	selector: 'sf-accounts-select',
	templateUrl: './accounts-select.component.html',
	styleUrls: ['./accounts-select.component.scss']
})
export class AccountsSelectComponent implements OnInit, OnDestroy {
	@Input() public accountName: string = null;
	@Input() public accountId: string = null;
	@Input() public skipSelection: boolean = false;
	@Input() public mode: NzSelectModeType = 'default';
	@Input() public loadedAccounts: Array<Account> = [];
	@Input() public selectLoadedAccounts: boolean = false;
	@Input() public placeholder: string = 'Type to search account';
	@Input() public disabled: boolean = false;
	@Output() public accountSelected = new EventEmitter<string>();
	@Output() public accountMultipleSelected = new EventEmitter<Array<string>>();
	@Output() public accountsOutput = new EventEmitter<Array<Account>>();

	public accounts: Array<Account> = [];
	public selectedAccount: string | Array<string> = [];
	public loading: boolean = true;
	public scrollLoading: boolean = false;
	public searchLoading: boolean = false;
	private pageIndex: number = 1;
	private searchValue: string = '';
	private totle: number = 0;
	private firstLoad: boolean = false;
	private readonly limit: number = 50;
	private searchTimer: Subscription = null;
	private accountsRequest$: Subscription = null;

	public constructor(private accountService: AccountService) { }

	public ngOnInit(): void {
		this.accountService.accountListPreviousParams = '';
		
		if (this.accountName) this.searchValue = this.accountName;

		if (this.accountId) {
			if (this.mode == 'default') this.selectedAccount = this.accountId;
			else this.selectedAccount = [this.accountId];
		}
		
		if (this.loadedAccounts?.length) {
			this.loading = false;
			this.accounts = this.loadedAccounts;
			this.pageIndex = parseInt((this.loadedAccounts.length / this.limit).toString()) + 1;

			if (this.selectLoadedAccounts) this.selectedAccount = this.loadedAccounts?.map((item: Account) => item?.uuid);
		} else {
			this.loadData();
		}
	}

	public ngOnDestroy(): void {
		this.accountsRequest$?.unsubscribe();
	}

	public search(text: string): void | boolean {
		this.searchTimer?.unsubscribe();

		if (text == this.searchValue) return false;

		this.searchValue = text;
		this.pageIndex = 1;
		this.searchLoading = true;
		this.accounts = [];

		this.searchTimer = timer(1000).subscribe(() => this.loadData());
	}

	public scrollToBottom(): void | boolean {
		if (this.scrollLoading || this.totle == this.accounts?.length) return false;

		this.scrollLoading = true;

		this.loadData();
	}

	public accountChange(): void {
		if (this.selectedAccount === null || (Array.isArray(this.selectedAccount) && !this.selectedAccount?.length)) {
			this.loading = true;
			this.pageIndex = 1;
			this.accounts = [];

			this.loadData();

			if (this.mode != 'default') this.accountMultipleSelected.emit([]);
		} else {
			if (this.mode == 'default') this.accountSelected.emit(<string>this.selectedAccount);
			else this.accountMultipleSelected.emit(<Array<string>>this.selectedAccount);
		}
	}

	private loadData(): void {
		let params: Partial<NzTableQueryParams> = {
			pageIndex: this.pageIndex,
			pageSize: this.limit
		};

		if (this.searchValue) params['searchValue'] = this.searchValue;

		if (this.mode != 'default') params['accountCount'] = this.selectedAccount?.length?.toString() || null;

		if (this.accountsRequest$) this.accountsRequest$?.unsubscribe();

		this.accountsRequest$ = this.accountService.getAccounts(params).subscribe((res: AccountsRO): void | Array<Account> => {
			if (!res?.result) return this.accounts = [];

			this.accounts = this.accounts.concat(res?.data?.items);
			this.totle = res?.data?.total_count;
			this.searchValue = '';
			this.pageIndex++;

			if (!this.firstLoad) {
				this.firstLoad = true;
				
				if (!this.skipSelection) this.selectedAccount = this.accounts?.[0]?.uuid;
				
				if (this.mode == 'default') this.accountSelected.emit(<string>this.selectedAccount);
				else this.accountMultipleSelected.emit(<Array<string>>this.selectedAccount);
			}

			this.accountsOutput.emit(this.accounts);
		});
		
		this.accountsRequest$.add(() => {
			if (this.loading) this.loading = false;
			if (this.searchLoading) this.searchLoading = false;
			if (this.scrollLoading) this.scrollLoading = false;
		});
	}
}